import { Injectable } from '@angular/core';
import {ReplaySubject, Subject} from "rxjs";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { UserCompanyGroup } from "../model/userCompanyGroup";
import { UserGroup } from "../model/userGroup";
import { PermissionEntry } from "../model/permissionEntry";
import { Order } from "../model/order";
import { User } from "../model/user";
import { Property } from "../model/property";
import { UserServiceService } from "./user-service.service";
import { PlatformService } from "./platform.service";
import {Router} from "@angular/router";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { NewOrderPageComponent } from "../component/new-order/new-order-page/new-order-page.component";
import { AuthGuard } from "../guards/auth.guard";
import {
  NewOrderPageConnectComponent
} from "../component/new-order/new-order-page-connect/new-order-page-connect.component";
import * as Sentry from "@sentry/browser";
import {PlatformProfile} from "../model/platformprofile";
import {CookieService} from "ngx-cookie-service";
import {AuthService} from "./auth.service";
import {CountryService} from "./country.service";

@Injectable({
  providedIn: 'root'
})
export class LoginIdService {
  private cancellationSubject = new Subject<void>();
  private loginIdSubject = new Subject<string | null>(); // track the current companyLoginId
  private initializedSubject = new Subject<boolean>();
  private loginStatusSubject = new Subject<boolean>();
  private loadingSavedPropertiesSubject = new ReplaySubject<boolean>(1);
  private headersSubject = new Subject<object>(); // track the current companyLoginHeader
  private currentUserCompanyGroup : UserCompanyGroup | null = null; // contains the currently selected UserCompanyGroup with its UserGroup
  private userCompanyGroups: UserCompanyGroup[] = [];
  private userPlatformMapping: Map<string, PlatformProfile> = new Map<string, PlatformProfile>();
  private userMultiUserMapping: Map<string, User> = new Map<string, User>();
  private currentLoginId: string | null = null; // the currentLoginId as plain variable instead of observable
  private user: User; // the currently logged-in User
  private currentMultiUser: User;
  private currentHeader: any; // the currentHeader as plain variable instead of observable
  private loginStatus: boolean = false;

  // Observable subjects
  loginIdChanged$ = this.loginIdSubject.asObservable();
  loginServiceInitialized$ = this.initializedSubject.asObservable();
  headersChanged$ = this.headersSubject.asObservable();
  loginStatusChanged$ = this.loginStatusSubject.asObservable();
  loadingSavedPropertiesChanged$ = this.loadingSavedPropertiesSubject.asObservable();

  constructor(private httpClient: HttpClient, private userService: UserServiceService, private platformService: PlatformService,
              private router: Router, private dbService: NgxIndexedDBService, private cookieService: CookieService, private authService: AuthService,
              private indexDBService: NgxIndexedDBService, private countryService: CountryService) {
    this.setUpUser();
    this.setUpUserPlatformMapping();
    this.setUpUserMultiUserMapping();
  }

  // when reloading the page try to set up the service with sessionStorage data
  setUpUser(): void {
    if(!this.user) {
      const parsedUser = JSON.parse(sessionStorage.getItem('user'));
      if(parsedUser) {
        this.user = parsedUser;
      }
    }
  }

    /**
     * Setup the mapping of userIds to their corresponding platform profiles from the session storage
     */
  public setUpUserPlatformMapping(): void{
      const userPlatformMappingFromSessionStorage = sessionStorage.getItem("user_platform_mapping");
      if(userPlatformMappingFromSessionStorage){
          this.userPlatformMapping = new Map(JSON.parse(userPlatformMappingFromSessionStorage));
      }
  }

    /**
     * Setup the mapping of userIds to their corresponding user objects from the session storage
     */
  public setUpUserMultiUserMapping(): void{
      const userMultiUserMappingFromSessionStorage = sessionStorage.getItem("user_multi_user_mapping");
      if(userMultiUserMappingFromSessionStorage){
          this.userMultiUserMapping = new Map(JSON.parse(userMultiUserMappingFromSessionStorage));
      }
  }

    /**
     * Update this.userPlatformMapping with a given updatedUserPlatformMapping
     *
     * @param updatedUserPlatformMapping Updated userPlatformMapping
     */
  public updatePlatformUserMapping(updatedUserPlatformMapping: Map<string, PlatformProfile>): void{
      this.userPlatformMapping = updatedUserPlatformMapping;
  }

  // used to notify subscribed components about MultiUser/LoginId changes
  updateLoginId(userCompanyGroup: UserCompanyGroup | null) {
    if(this.currentUserCompanyGroup && userCompanyGroup && userCompanyGroup.companyLoginId === this.currentUserCompanyGroup.companyLoginId){
        return
    }
    const oldGroup = this.currentUserCompanyGroup;
    this.currentUserCompanyGroup = userCompanyGroup;
    // we want to assign the currentLoginId after all requests are complete but need a temporary value before
    const tmpLoginId = userCompanyGroup ? userCompanyGroup.companyLoginId : null;
    if(tmpLoginId){
        this.getUserByLoginId(tmpLoginId).then(user => {
            this.currentMultiUser = user;
            sessionStorage.setItem('currentMultiUser', JSON.stringify(user));
            Sentry.getCurrentScope().setTag('currentMultiUserId', user.id);
            this.updateHeaders(tmpLoginId);
            // Fetch the countries after login and cache them, but only if not already cache
            this.countryService.loadAndCacheCountriesIfAbsent();
            // check if the change of the Multi-User-Access requires another PlatformProfile
            if(this.checkProfileChange(oldGroup)) {
                this.getPlatformProfile(this.currentHeader).then( () => {
                    this.finishLoginIdUpdate(tmpLoginId);
                    this.serviceInitialized();
                    this.needsPageReload();
                }, error => {
                    console.log(error);
                });
            } else {
                this.finishLoginIdUpdate(tmpLoginId);
                this.serviceInitialized();
            }
        })
    }
  }

    /**
     * Get a user Object for a specific loginId.
     * Tries to get the user object from the session storage userMultiUserMapping. If the user cannot
     * @param loginId
     * @private
     */
  private async getUserByLoginId(loginId: string): Promise<User> {
      if (this.userMultiUserMapping.has(loginId)) {
          return this.userMultiUserMapping.get(loginId);
      } else {
         const user = await this.userService.getUserById(loginId).toPromise();
         this.userMultiUserMapping.set(loginId, user);
         sessionStorage.setItem("user_multi_user_mapping", JSON.stringify(Array.from(this.userMultiUserMapping.entries())));
         return user;
      }
  }

  // update the loginState to show or hide login related components
  updateLoginStatus(status: boolean): void {
    this.loginStatus = status;
    this.loginStatusSubject.next(status);
  }

  finishLoginIdUpdate(loginId): void {
    this.currentLoginId = loginId;
    this.loginIdSubject.next(this.currentLoginId);
  }

  serviceInitialized(): void {
    this.initializedSubject.next(true);
    this.initializedSubject.complete();
  }

  // update the headers used for some of Multi-User related httpRequests
  private updateHeaders(loginId: string | null): void {
    if(loginId !== null) {
      const headers = this.getCustomHeaders(loginId);
      this.currentHeader = headers;
      this.headersSubject.next({ headers: headers });
    } else {
      const headers = { };
      this.currentHeader = headers;
      this.headersSubject.next(headers);
    }
  }

  createHeaders(loginId: string): Object {
    this.updateHeaders(loginId);
    return this.getCustomHeaders(loginId);
  }

  public setUserCompanyGroups(userCompanyGroups: UserCompanyGroup[]) : void {
    this.userCompanyGroups = userCompanyGroups;
  }

  public getUserCompanyGroups(): UserCompanyGroup[] {
      return this.userCompanyGroups && this.userCompanyGroups.length > 0  ? this.userCompanyGroups : [];
  }

  public updateUserCompanyGroupsPermissions(updatedGroups: UserCompanyGroup[]): void {
      updatedGroups.forEach( group => {
          const groupIndex = this.userCompanyGroups.findIndex(ucg => ucg.companyLoginId === group.companyLoginId);
          if(groupIndex !== -1) {
              this.userCompanyGroups[groupIndex].userGroup.permissions = group.userGroup.permissions;
          } else {
              console.log('Some permissions have been removed!');
          }
      });
  }

  public getCurrentMultiUser(): User {
    return this.currentMultiUser;
  }

  // when changing the Multi-User/Personal-Access check if the PlatformProfile has to be reloaded
  checkProfileChange(oldGroup: UserCompanyGroup | null): boolean {
    const defaultProfile = JSON.parse(sessionStorage.getItem('platform_default'));
    if(oldGroup && this.currentUserCompanyGroup)
      return oldGroup.platformProfileId !== this.currentUserCompanyGroup.platformProfileId;
    if(!oldGroup && this.currentUserCompanyGroup) { // during first login
      return defaultProfile.id !== this.currentUserCompanyGroup.platformProfileId;
    }
    if(oldGroup && !this.currentUserCompanyGroup)
      return oldGroup.platformProfileId !== defaultProfile.id;

    return false;
  }

  // authenticate the user and retrieve the user object
  async authenticate(credentials) {
    return await this.httpClient.post<any>("/authorize",
      JSON.stringify(credentials), {headers: new HttpHeaders({'Content-Type': 'application/json'})}).toPromise()
      .then(async () => {
          await this.userService.getUser().toPromise().then(data => {
              sessionStorage.setItem("user", JSON.stringify(data))
              this.user = data;
          })
      });
  }

    /**
     * Check if the current token is at least valid for the next minimumTimeInSeconds
     *
     * @param minimumTimeInSeconds Minimum time in seconds the token of authorizedClient should at least be valid
     * @return Promise containing a boolean which is true if the current token is valid for at least minimumTimeInSeconds else false
     */
  public async checkExpirationTime(minimumTimeInSeconds: number): Promise<boolean> {
      return await this.httpClient.get<boolean>(`/token-expiration-in?minimumTimeInSeconds=${minimumTimeInSeconds}`).toPromise()
              .catch((error) => {
                Sentry.captureException(error);
                return false
              });
  }

  // checks if the user is authenticated now and updates the loginStatus
  async authenticated() {
    return await this.httpClient.get("/authorized").toPromise().then(() => {
      this.updateLoginStatus(true);
    });
  }

  // fetch the platformProfile for the selected Multi-User/LoginId
    async getPlatformProfile(headers: any) {
        const loginId = headers.get("company");

        if (this.userPlatformMapping.has(loginId)) {
            const platformProfile = this.userPlatformMapping.get(loginId);
            this.updatePlatformInformation(platformProfile);
        } else {
            this.triggerCancellation();
            this.loadingSavedPropertiesSubject.next(true);
            const [profile, propertiesProfile] = await Promise.all([
                this.platformService.getPlatformprofile(headers, 'PLATFORM_CONNECT_BASE_VIEW').toPromise(),
                this.platformService.getPlatformprofile(headers, 'PLATFORM_CONNECT_PROPERTIES_VIEW').toPromise()
            ]);
            profile.properties = propertiesProfile.properties;
            this.userPlatformMapping.set(loginId, profile);
            sessionStorage.setItem("user_platform_mapping", JSON.stringify(Array.from(this.userPlatformMapping.entries())));
            sessionStorage.removeItem("platform_default");
            this.updatePlatformInformation(profile);
            this.loadingSavedPropertiesSubject.next(false);
        }
    }

    /**
     * Updates the platform profile information in the sessionStorage and adjusts the order page component based on the platform type
     *
     * @param platformProfile PlatformProfile which should be used to update the information
     */
    public updatePlatformInformation(platformProfile: PlatformProfile): void {
        sessionStorage.setItem("platform", JSON.stringify(platformProfile));

        if (sessionStorage.getItem("platform_default") === null) { // we can set the default Profile here
            sessionStorage.setItem("platform_default", JSON.stringify(platformProfile));
        }

        // change the OrderPageComponent depending on the platformType
        if (platformProfile.platformType === "PROPERTY" || platformProfile.platformType === "STEUER") {
            this.changeRoute(NewOrderPageComponent);
        } else if (platformProfile.platformType === "CONNECT" || platformProfile.platformType === "MANDATE") {
            this.changeRoute(NewOrderPageConnectComponent);
        }
    }

  // if the Multi-User Selection Change requires a new PlatformProfile navigate to /home
  private needsPageReload(): void {
    const url = this.router.url
    if (url.includes('new_order')) {
      // page reload currently not possible due to endless page reload cycle
      this.router.navigate(['/home']);
    }
    else if(url.includes('saved-properties/')){
        this.router.navigate(['/saved-properties'])
    }
  }

  // switch the newOrderPageComponent corresponding to the active PlatformProfile
  changeRoute(updatedComponent: any) {
    let index = this.router.config.findIndex(x => x.path === 'new_order');
    this.router.config.splice(index, 1);
    this.router.config.unshift(
        {path: 'new_order', component: updatedComponent, canActivate: [AuthGuard]}
    );
  }

    /**
     * Log out the current user. Removes the JSESSIONID cookie to make sure the user can always log in again
     *
     * @param shouldClearSessionStorage Boolean which determines if the session storage should be cleared when logging out or not
     */
    async loggedOut(shouldClearSessionStorage: boolean = true) {
      try {
        await this.indexDBService.clear('authorities').toPromise();
        if (shouldClearSessionStorage) {
          this.clearSessionStorage();
        }
        this.authService.logout();
        this.updateLoginStatus(false);
        this.updateLoginId(null)
        //Remove the JSESSIONID cookie to make sure the user can log in even if his token was revoked from any other source than his own logout call
        this.cookieService.delete("JSESSIONID", "/");
      } catch (error) {
        Sentry.captureException(error);
        console.log(error);
      }
    }

  // clear the sessionStorage with all platform related data
  clearSessionStorage() {
    this.dbService.clear('orders').subscribe();
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('loginId');
    sessionStorage.removeItem('currentMultiUser');
    sessionStorage.removeItem('platform');
    sessionStorage.removeItem('platform_default');
    sessionStorage.removeItem("mandate_id");
    sessionStorage.removeItem("orders_timestamp");
    sessionStorage.removeItem("new_order_properties");
    sessionStorage.removeItem('selectedUserGroup');
    sessionStorage.removeItem("interest");
    sessionStorage.removeItem("authorityData");
    sessionStorage.removeItem("subAuthorityData");
    sessionStorage.removeItem("customer_interactions");
    sessionStorage.removeItem('categories');
    sessionStorage.removeItem('products');
    sessionStorage.removeItem('bookableServices');
    sessionStorage.removeItem('serviceCategories');
    sessionStorage.removeItem('user_platform_mapping')
    sessionStorage.removeItem('savedPropertyDocuments')
    sessionStorage.removeItem('user_multi_user_mapping');
    sessionStorage.removeItem('firstLogin');
    sessionStorage.removeItem('countries');
    Sentry.configureScope(scope => scope.clear());
  }

  // clear some sessionStorage data to allow a clean login process
  clearSessionStorageBeforeLogin() {
    sessionStorage.removeItem("loginId");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem('currentMultiUser');
    sessionStorage.removeItem("platform");
    sessionStorage.removeItem("platform_default");
    sessionStorage.removeItem("mandate_id");
    sessionStorage.removeItem("orders_timestamp");
    sessionStorage.removeItem("savedPropertyDocuments");
    sessionStorage.removeItem("new_order_properties");
    this.dbService.clear('orders').subscribe();
  }

  // get the current LoginId or null when no Multi-User is selected without the need to subscribe to the observable Subject
  getCurrentLoginId(): string | null {
    return this.currentLoginId;
  }

  // get the current Login-State without the need to subscribe to the observable Subject
  getLoginStatus(): boolean {
    return this.loginStatus;
  }

  // get the currently active UserGroup if a Multi-User access is selected or else null
  getCurrentPermissions(): UserGroup | null {
    return this.currentUserCompanyGroup ? this.currentUserCompanyGroup.userGroup : null;
  }

  // get the currently active headers
  getCurrentHeaders(): any {
    return this.currentHeader;
  }

  // get a full httpOptions object to pass to httpRequests
  getCurrentHttpOptions(): any {
    return { headers: this.currentHeader };
  }

  // returns if the user has admin permissions within the active Multi-User access
  isAdmin(): boolean {
    return (
        this.currentUserCompanyGroup !== null &&
        this.currentUserCompanyGroup.userGroup !== undefined &&
        this.currentUserCompanyGroup.userGroup.roles.includes("ADMIN")
    )
  }

  // returns if the user is allowed to create new orders within the active Multi-User access
  canOrder(): boolean {
    return (
        this.currentUserCompanyGroup === null || // user wants to create a private order for himself
        (
            this.currentUserCompanyGroup.userGroup !== undefined && // user wants to order something for a companyLogin
            this.currentUserCompanyGroup.userGroup.can_order === true
        )
    )
  }

  // returns if the user has VIEW or DOWNLOAD permissions for the given order by checking the permissions object
  hasPermissionsForOrder(order: Order, permission: string = 'VIEW') {
    if(!this.loginStatus) return false;
    let entry = null;
    let result = false;

    if(this.currentUserCompanyGroup !== null) { // check if UserCompanyGroup is set
      entry = this.getCurrentPermissions().permissions.get(order.orderid); // do permissions for the order exist?
    }

    const privateOrder = this.user.id === order.userid; // is it a private order?

    if(this.isAdmin() || privateOrder) { // user is admin or its his own order
      result = this.isAdmin() || privateOrder;
    }

    if(entry) { // there is an entry within the permission, check if it contains permissions for download
      result = result || entry.scopes.includes(permission);
    }

    return result; // no permissions exist
  }

  // returns if the user has VIEW or DOWNLOAD permissions for the given property by checking the permissions object
  hasPermissionForProperty(property: Property, permission: string = 'VIEW') {
    let entry = null;
    let result = false;

    if(this.currentUserCompanyGroup !== null) { // check if UserCompanyGroup is set
      entry = this.getCurrentPermissions().permissions.get(property.orderid); // do permissions for the order exist?
    }

    const privateOrder = this.user.id === property.userid; // is it a private order?

    if(this.isAdmin() || privateOrder) { // user is admin or its his own order
      result = this.isAdmin() || privateOrder;
    }

    if(entry) { // there is an entry within the permission, check if it contains permissions for download
      result = result || entry.scopes.includes(permission);
    }

    return result; // no permissions exist
  }

  // returns the PermissionEntry for a given orderId if present within the active UserGroup
  getOrderPermissionEntry(orderId: string): PermissionEntry {
    if(this.getCurrentPermissions()) {
      return this.getCurrentPermissions().permissions.get(orderId);
    }
    return null;
  }

  // return the custom Multi-User header
  private getCustomHeaders(loginId: string): HttpHeaders {
    return new HttpHeaders().set('company', loginId);
  }

    /**
     * Returns the custom Multi-user header with the showing-spinner header to not show the spinner
     */
    public getCurrentCustomHeadersWithoutSpinner(): HttpHeaders {
        return new HttpHeaders().set('company', this.getCurrentLoginId()).set('showing-spinner', '');
    }

    /**
     * Emits a value to the cancellation subject, completes it and initialized it again for resetting.
     * Can be used to cancel requests if the cancellation subject emits a value
     */
    public triggerCancellation(): void{
        this.cancellationSubject.next();
        this.cancellationSubject.complete();
        this.cancellationSubject = new Subject<void>();
    }

}
